import { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { getSystemMetricsHistory } from '@/api/admin';
import type { SystemMetricsSample } from '@/types';

type RangeHours = 1 | 6 | 24 | 72 | 168;

function formatTime(label: unknown): string {
  const d = new Date(String(label));
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(label: unknown): string {
  const d = new Date(String(label));
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtPct(value: unknown, name: string): [string, string] {
  return [`${Number(value || 0).toFixed(1)}%`, name];
}

export function SystemMetricsChart() {
  const [range, setRange] = useState<RangeHours>(24);
  const [samples, setSamples] = useState<SystemMetricsSample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const history = await getSystemMetricsHistory(range);
      setSamples(history.samples);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const ranges: { value: RangeHours; label: string }[] = [
    { value: 1, label: '1h' },
    { value: 6, label: '6h' },
    { value: 24, label: '24h' },
    { value: 72, label: '3d' },
    { value: 168, label: '7d' },
  ];

  const tickFormatter = range <= 24 ? formatTime : formatDateTime;

  if (error) {
    return <div className="p-4 bg-destructive/10 text-destructive rounded-lg">{error}</div>;
  }

  if (loading && samples.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (samples.length === 0) {
    return (
      <div className="text-muted-foreground text-center py-12">
        No metrics data yet. Samples are collected every 60 seconds.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Range selector */}
      <div className="flex gap-2">
        {ranges.map(r => (
          <button
            key={r.value}
            onClick={() => setRange(r.value)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              range === r.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* CPU Chart */}
      <ChartCard title="CPU Usage (%)">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={samples}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="sampledAt" tickFormatter={tickFormatter} tick={{ fontSize: 11 }} className="text-muted-foreground" />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} className="text-muted-foreground" />
            <Tooltip
              labelFormatter={formatDateTime}
              formatter={(v: unknown) => fmtPct(v, 'CPU')}
              contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }}
            />
            <Area type="monotone" dataKey="cpuUsagePercent" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.2} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Memory Chart */}
      <ChartCard title="Memory Usage (%)">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={samples}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="sampledAt" tickFormatter={tickFormatter} tick={{ fontSize: 11 }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
            <Tooltip
              labelFormatter={formatDateTime}
              formatter={(v: unknown) => fmtPct(v, 'Memory')}
              contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }}
            />
            <Area type="monotone" dataKey="memoryUsagePercent" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Disk Chart */}
      <ChartCard title="Disk Usage (%)">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={samples}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="sampledAt" tickFormatter={tickFormatter} tick={{ fontSize: 11 }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
            <Tooltip
              labelFormatter={formatDateTime}
              formatter={(v: unknown) => fmtPct(v, 'Disk')}
              contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }}
            />
            <Area type="monotone" dataKey="diskUsagePercent" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.2} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* DB Pool Chart */}
      <ChartCard title="Database Pool">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={samples}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="sampledAt" tickFormatter={tickFormatter} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip
              labelFormatter={formatDateTime}
              contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }}
            />
            <Legend />
            <Bar dataKey="dbPoolActive" stackId="pool" fill="hsl(var(--primary))" name="Active" />
            <Bar dataKey="dbPoolIdle" stackId="pool" fill="hsl(var(--muted))" name="Idle" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-card p-4 rounded-lg border border-border">
      <h4 className="text-sm font-semibold mb-3 text-muted-foreground">{title}</h4>
      {children}
    </div>
  );
}
